// A site video's transcript reaches the inspector notes once (2026-10-28).
//
// RBB/BRC (1026) - UNIT 17 (RJ BIRD, order 612889) held each of its three
// transcripts twice. Two writers put a clip's transcript into the notes:
//
//   - attended Stage 1, when it reads the clip (stage1-video-import-run.ts)
//   - evidence preparation, before every Summary and Quote Run
//     (project-video-evidence-preparation.ts)
//
// Preparation skipped a clip whose block was already in the notes by looking
// for its exact HTML. CN5 (2026-10-14) took the file name out of the Stage 1
// header, the two blocks stopped being equal, and preparation wrote every
// attended clip a second time at the first Summary. Matching HTML was never
// safe anyway: the notes editor rewrites the markup it holds.
//
// The rule rests on the clip's transcript record instead of the notes text.
// The notes take a transcript when it is first recorded for its clip, and
// never again. Each writer that records a transcript writes the notes in the
// same step (a folder restore brings the saved notes with it), so a clip that
// already holds a transcript has its words in the notes, or the operator took
// them out on purpose. Both writers build the block here, so it is one block.
//
// The same record says whether a clip is transcribed on the Files tab. A clip's
// video row can lose its `processed` and `transcript` to a stale write while
// its transcript row keeps them (local 261010, clip 0065), and the Files tab
// read only the video row, so it said "Not transcribed" over words the notes
// held.
//
// A part of a split video (stage1-video-split-tracking.ts) is a clip of its
// own here, and its header names its part and time range instead, so the
// later prompts read the parts as one walkthrough.
import { notesImportHeader } from '../utils/ai-document-label';
import type { SplitSourceDescriptor } from './stage1-video-split-tracking';

type Row = Record<string, any>;
type SplitPart = Pick<SplitSourceDescriptor, 'partIndex' | 'partCount' | 'startSeconds' | 'endSeconds'>;

function clock(seconds: number): string {
    const whole = Math.max(0, Math.floor(Number(seconds) || 0));
    return `${String(Math.floor(whole / 60)).padStart(2, '0')}:${String(whole % 60).padStart(2, '0')}`;
}

/** "part 2 of 3, 04:30 to 09:00": what a part's transcript header and messages say. */
export function splitPartLabel(descriptor: SplitPart): string {
    return `part ${descriptor.partIndex} of ${descriptor.partCount}, ${clock(descriptor.startSeconds)} to ${clock(descriptor.endSeconds)}`;
}

/** The part a split video's row or File is: the given descriptor, else the saved row's `splitSource`. Null for a whole clip. */
function splitPartFor(video: unknown, splitPart: SplitPart | null | undefined): SplitPart | null {
    const candidate = splitPart ?? (video && typeof video === 'object' ? (video as Row).splitSource : null);
    return candidate && Number.isFinite(Number(candidate.partIndex)) && Number.isFinite(Number(candidate.partCount))
        ? candidate as SplitPart
        : null;
}

/** The transcript a project records for a clip: its transcript row, else the text on its video row. Empty when it has none. */
export function recordedVideoTranscript(video: unknown, videoTranscripts: unknown): string {
    const clip = (video && typeof video === 'object' ? video : {}) as Row;
    const rows = (Array.isArray(videoTranscripts) ? videoTranscripts : []) as Row[];
    const row = clip.id == null ? null : rows.find(entry => entry?.videoId === clip.id);
    return String(row?.text || row?.fullText || row?.transcript || clip.transcript || '').trim();
}

/**
 * What one clip's transcript adds to the notes, before HTML formatting. The
 * header names no file (CN5). A part of a split video passes its descriptor
 * (a File from this session's split) or carries it as `splitSource` (a row).
 */
export function videoTranscriptNotesText(video: unknown, transcript: string, splitPart?: SplitPart | null): string {
    const part = splitPartFor(video, splitPart);
    const header = part ? `--- VIDEO TRANSCRIPT (${splitPartLabel(part)}) ---` : notesImportHeader(video, 'video');
    return `${header}\n\n${transcript}`;
}

/** Whether a newly recorded transcript goes into the notes: only for a clip that held none before. */
export function transcriptBelongsInNotes(previousTranscript: unknown, transcript: unknown): boolean {
    return !String(previousTranscript ?? '').trim() && !!String(transcript ?? '').trim();
}
